import LocalizationService from "services/localizationService";
import { computed } from "mobx";
import Enum from "models/enum";
import EnumService from "services/enumService";
import ChargeTypesService from "services/chargeTypesService";
import { bound } from "@frui.ts/helpers";
import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { interfaces } from "inversify";
import FuelTypeResponse from "entities/fuelTypeResponse";
import ChargeOrderService from "services/chargeOrderService";
import TimeService from "services/timeService";

export default class OrderRecapitulationViewModel extends ScreenBase {
  constructor(
    public chargeOrderService: ChargeOrderService,
    public timeService: TimeService,
    public localizationService: LocalizationService,
    private enumService: EnumService,
    private chargeTypesService: ChargeTypesService
  ) {
    super();

    this.initCountriesEnum();
  }

  private initCountriesEnum() {
    this.enumService.values("countries");
  }

  protected async onActivate() {
    return super.onActivate();
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get chargeOrderItem() {
    return this.chargeOrderService.chargeOrderItem;
  }

  @computed
  get chargeOrder() {
    return this.chargeOrderService.chargeOrder;
  }

  @computed
  get chargeType() {
    return this.chargeTypesService.findChargeType(this.chargeOrderItem._chargeTypeId);
  }

  @computed
  get emptyState() {
    return this.localizationService.translateGeneral("eshop.order.summary.empty");
  }

  @computed
  get afterValidityStart() {
    return this.chargeOrderItem.validity.validSinceDate && this.chargeOrderItem.validity.validSinceDate < this.serverTime;
  }

  @computed
  get vehicleSummaryInfo() {
    void this.enumService.isEnumLoaded("countries");
    const { countryId, licensePlate } = this.chargeOrderItem;
    const country = this.enumService.values("countries").find((country: Enum) => country.id === countryId);

    return countryId && licensePlate && country && country.code ? `${country.code} – ${licensePlate}` : this.emptyState;
  }

  @computed
  get validitySinceInfo() {
    if (this.chargeOrderItem.validity.validSinceDate !== undefined) {
      return this.chargeOrderItem.validity.validSinceDate;
    } else {
      return null;
    }
  }

  @computed
  get validityUntilInfo() {
    if (this.chargeOrderItem.validity._validUntilDate !== undefined) {
      return this.chargeOrderItem.validity._validUntilDate;
    } else {
      return null;
    }
  }

  @computed
  get emailInfo() {
    const emailAddress = this.chargeOrder.emailAddress;

    return emailAddress ? emailAddress : this.emptyState;
  }

  @computed
  get fuelTypeInfo() {
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    const fuelType = fuelTypes.find(fuelType => fuelType.id === this.chargeOrderItem._fuelTypeId)?.fuelType;

    return fuelType ? this.localizationService.translateAttribute("fuelType", fuelType) : this.emptyState;
  }

  @computed
  get markSummaryInfo() {
    if (!this.chargeType) {
      return this.emptyState;
    }
    return `${this.localizationService.translateAttribute("chargeType", this.chargeType.validFor)} `;
  }

  @computed
  get priceSummaryInfo() {
    const price = this.chargeOrderItem._priceListItems.find(
      item => item.chargeTypeId === this.chargeOrderItem._chargeTypeId
    )?.price;

    if (!price) {
      return this.emptyState;
    }

    return this.localizationService.formatPrice(price);
  }

  @computed
  get price() {
    const price = this.chargeOrderItem._priceListItems.find(
      item => item.chargeTypeId === this.chargeOrderItem._chargeTypeId
    )?.price;

    if (!price) {
      return undefined;
    }

    return price;
  }

  @bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return () => {
      return new OrderRecapitulationViewModel(
        container.get(ChargeOrderService),
        container.get(TimeService),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(ChargeTypesService)
      );
    };
  }
}
